export default class Dialog {
  /**
   * The Dialog component.
   *
   * @param {string|object} dialogId The ID of the dialog.
   * @param {string|object} buttonId The ID of the dialog-toggle.
   */
  constructor(dialogId, buttonId) {
    if (!dialogId) {
      return;
    }

    // Set from the dialogId if buttonId is not provided.
    if (!buttonId) {
      const dialogId = 'string' === typeof dialogId ? dialogId : dialogId?.id;
      if (!!dialogId) {
        buttonId = document.querySelector( `[aria-controls="${dialogId}"][aria-haspopup="dialog"]` );
      }
    }

    this.isOpen = false;
    this.dialog = 'string' === typeof dialogId ? document.getElementById( dialogId ) : dialogId
    this.openButton = !! buttonId && 'string' === typeof buttonId ? document.getElementById( buttonId ) : buttonId
    this.initialized = !!this.dialog && !!this.openButton;

    this.setupListeners();
  }

  setupListeners() {
    if (!this.initialized) {
      if (!!this.openButton) {
        // Set disabled if button exists but dialog does not.
        this.openButton.disabled = true;
      }

      return;
    }

    if (this.openButton) {
      this.openButton.addEventListener( 'click', (e) => {
        e.preventDefault();
        this.open()
      } );
    }

    const closeButton = this.dialog.querySelector( '.dialog__close-btn' );
    if (closeButton) {
      closeButton.addEventListener( 'click', (e) => {
        e.preventDefault();
        this.close()
      } );
    }

    // Add event listener for the Escape key.
    document.addEventListener( 'keydown', (event) => this.handleKeyPress( event ) );

    // Add event listener for clicking on the backdrop.
    this.dialog.addEventListener( 'click', (event) => this.handleBackdropClick( event ) );

    // Add event listener for managing focus.
    this.dialog.addEventListener( 'keydown', (event) => this.handleFocusIn( event ) );
  }

  open() {
    if (!this.initialized) {
      return;
    }
    this.isOpen = true;
    this.dialog.showModal();
  }

  close() {
    if (!this.initialized) {
      return;
    }
    this.isOpen = false;
    this.dialog.close();
    this.dialog.classList.remove( 'dialog--open' );

    if (this.dialog.hasAttribute( 'data-status' )) {
      switch (this.dialog.getAttribute( 'data-status' )) {
        case 'event-unsubscribed':
        case 'event-applied':
          // Force reload the page. Now we're sure all the data is up-to-date.
          window.top.location = window.top.location;
          break;
      }
    }
  }

  /**
   * Close the modal when the Escape key is pressed.
   *
   * @param {object} event Current event.
   */
  handleKeyPress(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /**
   * Close the modal when clicking on the backdrop.
   *
   * @param {object} event Current event.
   */
  handleBackdropClick(event) {
    if (event.target === this.dialog) {
      this.close();
    }
  }

  /**
   * Creates tab trap for the modal, so that the user can't tab out of the modal.
   *
   * @param {object} event Current event.
   */
  handleFocusIn(event) {
    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = this.dialog?.querySelectorAll( 'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])' );

    if (0 === focusableElements.length) {
      // Nothing to focus.
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (firstElement === lastElement) {
      // Already focussing with keyboard.
      return;
    }

    if (event.target === lastElement && !event.shiftKey) {
      // If the last focusable element is reached, shift focus to the first element
      event.preventDefault();
      firstElement.focus();
    } else if (event.target === firstElement && event.shiftKey) {
      // If the first focusable element is reached, shift focus to the last element
      event.preventDefault();
      lastElement.focus();
    }
  }
}
